import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { useProfile } from '@/auth/ProfileContext'
import { profileComplete, type Task, type User } from '@/lib/types'
import { introductionFor, INTRODUCTION_VERSION } from '@/lib/introduction'
import { isScienceExpert } from '@/lib/programs'
import { Icon } from './Icon'
import { Markdown } from './Markdown'
import './welcome-tutorial.css'

/** A read-only tour: navigation never creates a draft, claims a review, or submits work. */
export function WelcomeTutorial() {
  const { profile, setProfile } = useProfile()
  const science = isScienceExpert(profile)
  const cards = introductionFor(science)
  const navigate = useNavigate()
  const location = useLocation()
  const dialog = useRef<HTMLDialogElement>(null)
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [tasks, setTasks] = useState<Task[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const card = cards[step]
  useEffect(() => {
    const replay = () => { setStep(0); setOpen(true) }
    window.addEventListener('expert-introduction', replay)
    return () => window.removeEventListener('expert-introduction', replay)
  }, [])
  useEffect(() => {
    if (profile?.role === 'sme' && profileComplete(profile) && profile.onboarding_version === 1
      && (!profile.tutorial || profile.tutorial.status === 'active')) {
      // Old active tours start at the new welcome. Completed/skipped tours stay closed.
      setStep(profile.tutorial?.version === INTRODUCTION_VERSION ? Math.min(profile.tutorial.step, cards.length - 1) : 0)
      setOpen(true)
    }
  }, [profile])
  useEffect(() => {
    if (open && !dialog.current?.open) dialog.current?.showModal()
    if (!open && dialog.current?.open) dialog.current.close()
    const page = document.getElementById('main-content')
    page?.classList.toggle('tutorial-page', open)
    return () => page?.classList.remove('tutorial-page')
  }, [open])
  useEffect(() => {
    if (!open) return
    if (science) { setTasks([]); return }
    let alive = true
    api.get<{ tasks: Task[] }>('/sme/dashboard').then(data => { if (alive) setTasks(data.tasks) })
      .catch(() => { if (alive) setTasks([]) })
    return () => { alive = false }
  }, [open, science])

  const draft = tasks?.find(task => task.stage === 0)
  const execution = tasks?.find(task => task.stage === 2)
  const quality = tasks?.find(task => task.stage === 4)
  const examplePath = '/sme/introduction/example'
  const example = (section: string) => `${examplePath}?section=${section}`
  const empty = tasks?.length === 0
  const dashboard = empty ? example('pipeline') : '/sme'
  const draftPage = (section: string) => draft ? `/sme/tasks/${draft.task_id}/edit?section=${section}` : example(section)
  const destinations = science ? [
    [example('pipeline'), ''], [example('pipeline'), 'task-pipeline'], ['/sme/profile', 'profile-expertise'],
    [example('pipeline'), 'task-pipeline'], [example('template'), 'example-template'],
    [example('pipeline'), 'waiting-for-you'], [example('quality'), 'example-quality'],
    ['/sme/guide', 'guide-replay'],
  ] : [
    [dashboard, ''], [dashboard, 'task-pipeline'], ['/sme/profile', 'profile-expertise'],
    [dashboard, 'task-pipeline'], [draftPage('definition'), 'draft-definition'],
    [draftPage('brief'), 'draft-brief'], [draftPage('rubric'), 'draft-rubric'],
    [example('coach'), 'example-coach'],
    [execution ? `/sme/tasks/${execution.task_id}?section=work` : example('execution'), 'execution-materials'],
    [example('peer'), 'review-queue'],
    [quality ? `/sme/tasks/${quality.task_id}?section=work` : example('quality'), quality ? 'task-work' : 'example-quality'],
    ['/sme/guide', 'guide-replay'],
  ]
  const [destination, target] = destinations[step]
  useEffect(() => {
    if (!open || tasks === null) return
    if (`${location.pathname}${location.search}` !== destination
      || (destination === '/sme/guide' && location.state?.tutorialReference !== step)) {
      navigate(destination, { state: { tutorialReference: step }, replace: true })
    }
  }, [open, step, tasks, destination, navigate, location.pathname, location.search, location.state?.tutorialReference])
  useEffect(() => {
    if (!open || !target) return
    let highlighted: HTMLElement | null = null
    const observer = new MutationObserver(mark)
    function mark() {
      const element = document.querySelector<HTMLElement>(`[data-tour="${target}"]`)
      if (!element || element === highlighted || !element.getClientRects().length) return
      highlighted?.classList.remove('tutorial-highlight')
      highlighted = element
      element.classList.add('tutorial-highlight')
      element.scrollIntoView({ block: window.matchMedia('(max-width: 760px)').matches ? 'start' : 'center', behavior: 'instant' })
    }
    mark()
    observer.observe(document.getElementById('main-content')!, { childList: true, subtree: true })
    return () => { observer.disconnect(); highlighted?.classList.remove('tutorial-highlight') }
  }, [open, target, location.pathname, location.search, location.state?.tutorialReference])

  async function save(next: number, status: 'active' | 'completed' | 'skipped') {
    if (busy) return
    setBusy(true); setError('')
    try {
      const user = await api.put<User>('/me', { tutorial: { step: next, status, version: INTRODUCTION_VERSION } })
      setProfile(user); setStep(next)
      if (status !== 'active') {
        setOpen(false)
        if (location.pathname === examplePath) navigate('/sme', { replace: true })
      }
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  return <dialog ref={dialog} className="welcome-tutorial" aria-labelledby="tutorial-title"
    onCancel={e => { e.preventDefault(); void save(step, 'skipped') }}>
    <div className="tutorial-heading"><span className="tutorial-icon"><Icon name={step < 2 ? 'sparkles' : 'book'} size={22} /></span><div><p className="tutorial-kicker">Welcome to your workspace</p><p className="muted small">{step + 1} of {cards.length}</p></div></div>
    <h2 id="tutorial-title">{card.title}</h2><Markdown source={card.body} />
    {destination.startsWith(examplePath) && <p className="tutorial-context">{science ? 'This is example data. It does not change your saved work.' : 'This is example data. It does not change your tasks.'}</p>}
    {error && <p className="note err" role="alert">{error}</p>}
    <div className="tutorial-actions">
      <button className="ghost" disabled={busy} onClick={() => void save(step, 'skipped')}>Skip tour</button>
      <div className="tutorial-arrows"><button aria-label="Previous slide" title="Previous slide" disabled={busy || step === 0} onClick={() => void save(step - 1, 'active')}><Icon name="arrow" style={{ transform: 'rotate(180deg)' }} /></button>
      <button className="primary" aria-label={step === cards.length - 1 ? 'Finish introduction' : 'Next slide'} title={step === cards.length - 1 ? 'Finish introduction' : 'Next slide'} disabled={busy} onClick={() => void save(Math.min(step + 1, cards.length - 1), step === cards.length - 1 ? 'completed' : 'active')}><Icon name={step === cards.length - 1 ? 'check' : 'arrow'} /></button></div>
    </div>
    <progress aria-label="Introduction progress" max={cards.length} value={step + 1} />
  </dialog>
}
